import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import Close16 from '@carbon/icons-react/es/close/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import UserMultiple16 from '@carbon/icons-react/es/user--multiple/16';
import User16 from '@carbon/icons-react/es/user/16';
import { Dropdown } from 'antd';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { View } from 'api/views/types';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { Views } from 'components/molecules/views';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import { getDashboardViewsQueryKey } from 'queries/dashboard';
import { queueMacrotask } from 'utils/macrotask';
import {
  DEFAULT_VIEW_ID,
  DashboardFiltersViewDefinition,
} from './dashboard-filter-utils';

const StyledMenu = styled(Menu)`
  max-height: 45vh;
  height: 100%;
  overflow: scroll;
`;
const MenuHeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const MenuItemWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;
const FilterSelectedLabelWrapper = styled(MenuHeadingWrapper)``;

export interface ViewSelectProps {
  dashboardId: number;
  views: Views<DashboardFiltersViewDefinition>;
  selectedViewId: number;
  setViewId: (viewId, replace) => void;
  setFilter: (filter) => void;
  clearFilter: () => void;
  onViewDelete: (
    view: View<DashboardFiltersViewDefinition>,
  ) => Promise<View<DashboardFiltersViewDefinition>>;
  onViewEdit: (view: View<DashboardFiltersViewDefinition>) => void;
}

export const DashboardSavedFiltersSelect: FC<ViewSelectProps> = ({
  dashboardId,
  views,
  selectedViewId,
  setViewId,
  setFilter,
  clearFilter,
  onViewDelete,
  onViewEdit,
}) => {
  const translate = useTranslate();
  const [, saveViewId] = useLocalStorage(
    `dashboards.${dashboardId}.view_id`,
    DEFAULT_VIEW_ID.toString(),
  );

  const handleOnSelect = ({ key }) => {
    const filter = views?.byIds?.[key]?.[1]?.viewDefinition?.filter;

    if (filter) {
      setFilter(filter);
    } else {
      clearFilter();
    }
    queueMacrotask(() => setViewId(key, true));
  };

  const onViewClear = useCallback(() => {
    saveViewId(DEFAULT_VIEW_ID.toString());
    queueMacrotask(() => {
      setViewId(DEFAULT_VIEW_ID, false);
      clearFilter();
    });
  }, [clearFilter, saveViewId, setViewId]);

  const onDelete = useCallback(
    (view: View<DashboardFiltersViewDefinition>) => {
      confirm({
        okText: translate('delete'),
        okType: 'danger',
        cancelText: translate('cancel'),
        onCancel: () => {},
        title: translate('filter.delete_confirmation.title'),
        content: translate('filter.delete_confirmation.content', {
          viewName: view.viewName,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        onOk: () => {
          if (view.viewId === selectedViewId) {
            onViewClear();
          }

          onViewDelete(view)
            .then(() => {
              toastService({
                title: translate('filter.delete.success.title'),
                message: translate('filter.delete.success', {
                  viewName: view.viewName,
                }),
              }).success();
              queryClient.refetchQueries(getDashboardViewsQueryKey());
            })
            .catch(() => {
              toastService({
                title: translate('failure'),
                message: translate('filter.delete.failed', {
                  viewName: view.viewName,
                }),
              }).error();
            });
        },
      });
    },
    [onViewClear, onViewDelete, selectedViewId, translate],
  );

  const selectedView = views?.byIds?.[selectedViewId]?.[1];

  const viewsByIds = views?.byIds;

  const privateFilters = (views?.ids[1] ?? [])
    .map((id) => viewsByIds?.[id]?.[1])
    .filter((view) => view?.self);

  const sharedFilters = (views?.ids[1] ?? [])
    .map((id) => viewsByIds?.[id]?.[1])
    .filter((view) => !view?.self);

  const deleteMenu = (view) => (
    <Dropdown
      overlay={
        <Menu>
          <Menu.Item
            key="edit"
            icon={<Edit16 />}
            onClick={() => onViewEdit(view)}
          >
            <FormattedMessage id="edit" />
          </Menu.Item>
          <Menu.ItemDelete
            onClick={() => {
              onDelete(view);
            }}
          />
        </Menu>
      }
      trigger={['hover']}
      placement="bottomLeft"
    >
      <Button type="text" icon={<OverflowMenuVertical16 />} size="small" />
    </Dropdown>
  );

  return (
    <Dropdown
      trigger={['click']}
      overlay={
        <StyledMenu
          data-cy="views.dashboard.filter"
          selectedKeys={[String(selectedViewId)]}
          onClick={handleOnSelect}
        >
          {privateFilters.length ? (
            <>
              <Menu.Header>
                <MenuHeadingWrapper>
                  <User16 /> {translate('dashboard.my.saved.filter')}
                </MenuHeadingWrapper>
              </Menu.Header>
              {privateFilters.map((view) => (
                <Menu.Item
                  key={view.viewId}
                  onClick={handleOnSelect}
                  data-cy={`dashboard-filter-${view.viewName}`}
                >
                  <MenuItemWrapper>
                    {view.viewName}
                    {deleteMenu(view)}
                  </MenuItemWrapper>
                </Menu.Item>
              ))}
            </>
          ) : null}
          {sharedFilters.length ? <Menu.Divider /> : null}
          {sharedFilters.length ? (
            <>
              <Menu.Header>
                <MenuHeadingWrapper>
                  <UserMultiple16 /> {translate('dashboard.shared.filter')}
                </MenuHeadingWrapper>
              </Menu.Header>
              {sharedFilters.map((view) => (
                <Menu.Item
                  key={view?.viewId}
                  data-cy={`dashboard-filter-${view.viewName}`}
                >
                  <MenuItemWrapper>
                    {view.viewName}
                    {deleteMenu(view)}
                  </MenuItemWrapper>
                </Menu.Item>
              ))}
            </>
          ) : null}
        </StyledMenu>
      }
    >
      {selectedView ? (
        <Button size="small" type="text">
          <FilterSelectedLabelWrapper>
            <div>{translate('dashboard.saved.filter')}:</div>
            <strong>{selectedView.viewName}</strong>
            <Close16 onClick={onViewClear} />
          </FilterSelectedLabelWrapper>
        </Button>
      ) : !selectedView && (sharedFilters.length || privateFilters.length) ? (
        <Button type="link">
          <FilterSelectedLabelWrapper>
            <div>{translate('dashboard.choose.filter')}</div>
            <ChevronDown16 />
          </FilterSelectedLabelWrapper>
        </Button>
      ) : (
        <></>
      )}
    </Dropdown>
  );
};
